import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ReactComponent as ReceivedIcon } from '../assets/svgs/Received.svg';
import { ReactComponent as BoxIcon } from '../assets/svgs/Box.svg';
import { ReactComponent as KitchenUtensilsIcon } from '../assets/svgs/Kitchen Utensils.svg';
import { DropLocation, Order } from '@m4a3/api-client';
import { logToDatadog } from './datadog';
import { API } from 'aws-amplify';
import { store } from '../store';
import { getOrder } from '../actions/orderActions';
import axios from 'axios';
import { getLatestJwtToken } from './amplify-auth';
import { StatusType } from '@datadog/browser-logs';

export const getNextStatus = (status: string): string => {
  const mapping = {
    ACCEPTED: 'STORE_ACCEPTED',
    STORE_ACCEPTED: 'STORE_PREPARING',
    STORE_PREPARING: 'STORE_PREPARED',
    STORE_PREPARED: 'RECEIVED_FROM_STORE',
    RECEIVED_FROM_STORE: 'READY',
  }

  return mapping[status] || null;
};

export const getStatusData = (status: string) => {
  const defaultValue = {
    icon: null,
    colour: '',
    text: '',
    shortText: ''
  };
  const statusDataMap = {
    CREATED: {
      icon: StarBorderRoundedIcon,
      colour: '#EAAC30',
      text: 'New order',
      shortText: 'New'
    },
    REJECTED: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Order rejected',
      shortText: 'Rejected'
    },
    ACCEPTED: {
      icon: ThumbUpOutlinedIcon,
      colour: '#610BEF',
      text: 'Manna accepted',
      shortText: 'Accepted'
    },
    STORE_ACCEPTED: {
      icon: ThumbUpOutlinedIcon,
      colour: '#FC5000',
      text: 'Store accepted',
      shortText: 'Store Accepted'
    },
    STORE_REJECTED: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Store rejected',
      shortText: 'Rejected'
    },
    RESET: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Reset',
      shortText: 'Reset'
    },
    STORE_PREPARING: {
      icon: KitchenUtensilsIcon,
      colour: '#14142B',
      text: 'Preparing',
      shortText: 'Preparing'
    },
    STORE_UNAVAILABLE: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Rejected',
      shortText: 'Unavailable'
    },
    STORE_PREPARED: {
      icon: KitchenUtensilsIcon,
      colour: '#14142B',
      text: 'Prepared',
      shortText: 'Prepared'
    },
    CANCELLED: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Cancelled',
      shortText: 'Cancelled'
    },
    RECEIVED_FROM_STORE: {
      icon: ReceivedIcon,
      colour: '#008A00',
      text: 'Received',
      shortText: 'Received'
    },
    READY: {
      icon: BoxIcon,
      colour: '#008A00',
      text: 'Ready',
      shortText: 'Ready'
    },
    DISPATCHED: {
      icon: CloudQueueRoundedIcon,
      colour: '#0583FF',
      text: 'Dispatched',
      shortText: 'Dispatched'
    },
    ABORTED: {
      icon: CancelOutlinedIcon,
      colour: '#A0A3BD',
      text: 'Aborted',
      shortText: 'Aborted'
    },
    DELIVERED: {
      icon: CheckCircleOutlineIcon,
      colour: '#14142B',
      text: 'Delivered',
      shortText: 'Delivered'
    },
    COMPLETE: {
      icon: CheckCircleOutlineIcon,
      colour: '#A0A3BD',
      text: 'Completed',
      shortText: 'Completed'
    },
    COMPLETE_BYCAR: {
      icon: CheckCircleOutlineIcon,
      colour: '#A0A3BD',
      text: 'Complete by car',
      shortText: 'By Car'
    }
  };

  return statusDataMap[status] || defaultValue;
};

export const orderIsAssignedToAircraft = (order: Order, aircraft: string, strictCheck = false): boolean => {
  if ((!order?.aircraft || aircraft === 'None') && !strictCheck) {
    return true;
  }

  return order?.aircraft === aircraft;
}

export const orderIsInLandingZone = (order: Order, landingZoneId: string): boolean => {
  return order?.landingZoneId === landingZoneId;
}

export const orderIsInCellId = (order: Order, cellId: number): boolean => {
  return order?.cellId === cellId;
}

export const orderIsInLane = (order: Order, lane: string, strictCheck = false): boolean => {
  if (lane === '' && !strictCheck) {
    return true;
  }
  return order?.tags?.includes(lane)
}

export async function addMcSelectedDpToOrder(order: Order): Promise<Order> {
  try {
    const latestOrder = await getOrder(order.id)(store.dispatch);
    const body = Object.assign({}, latestOrder, {mcHasConfirmedDropPoint: true});
    const updatedOrder = await API.put('api', `/order/${order.id}`, {body})

    logToDatadog('update_order addMcSelectedDpToOrder', { updatedOrder, originalOrder: latestOrder });

    return updatedOrder;
  } catch (error) {
    logToDatadog('error getting/updating the order', { error, order })
    return Object.assign({}, order, { mcHasConfirmedDropPoint: true })
  }
}

export async function updateDropLocationOnOrder(orderID: string, dropPoint: DropLocation): Promise<Order> {
  try {
    const latestOrder = await getOrder(orderID)(store.dispatch);
    const body: Order = Object.assign({},
      latestOrder,
      {
        dropLatlng: `${dropPoint.latitude},${dropPoint.longitude}`,
        dropLocationId: dropPoint.id,
      });
    const updatedOrder = await API.put('api', `/order/${orderID}`, {body})

    logToDatadog('update_order updateDropLocationOnOrder', { updatedOrder, originalOrder: latestOrder });

    return updatedOrder;
  } catch (error) {
    logToDatadog('error getting/updating the order', { error, orderID });
  }
}

/**
 * Updates order status using the new ORDERFUL API endpoint
 * @param orderId - The order ID to update
 * @param status - The new status value
 * @returns Promise that resolves when the update is complete
 */
export async function updateOrderStatus(orderId: string, status: string, reason?: string): Promise<void> {
  try {
    const baseUrl = process.env.REACT_APP_ORDERFUL_URL;
    const token = await getLatestJwtToken();
    
    const body: any = { status };
    if (reason) {
      body.reason = reason;
    }

    await axios.patch(`${baseUrl}/orders/${orderId}/status`, body, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    logToDatadog('order_status_updated_success', { orderId, status, reason });
  } catch (error) {
    logToDatadog('order_status_update_failed', { orderId, status, reason, error }, StatusType.error);
    throw error;
  }
}
